import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AlertTriangle, CheckCircle, Copy, Send, X } from 'lucide-react';
import type {
  EmployeeDesignation,
  EmployeeInviteRole,
  SupplierPortalRole,
  SupplierWorkFlag,
} from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { EMPLOYEE_DESIGNATIONS } from '../../utils/employeeDesignations';
import {
  supplierWorkFlagsFromOrgCapabilities,
  supplierWorkFlagLabel,
  supplierPortalRoleLabel,
} from '../../utils/supplierWorkFlags';
import { crmAuthedFetch } from '../../utils/crmAuthedFetch';
import { db } from '../../utils/firebase';
import { grantArchitectPortalProjects } from '../../utils/grantArchitectPortalAccess';
import { architectDealProjectIds } from '../../utils/architectProjectAccess';
import { normalizeWorkstationUsername, resolveInviteAuthEmail } from '../../utils/workstationLogin';
import './UserManagement.css';

export type UnifiedInviteRole =
  | 'employee'
  | 'employee_shop'
  | 'employee_office'
  | 'admin'
  | 'staff'
  | 'designer'
  | 'architect'
  | 'engineer'
  | 'cpa'
  | 'supplier';

const FUNCTIONS_URL = 'https://us-central1-lctf-projects.cloudfunctions.net';

const ROLE_OPTIONS: Array<{ role: UnifiedInviteRole; label: string; hint: string }> = [
  { role: 'employee', label: 'Employee — Full', hint: 'Leads, Pipeline, full Projects menu, workspace Settings.' },
  { role: 'employee_shop', label: 'Employee — Shop', hint: 'W-2 — Project Hub, Operations, Purchasing only.' },
  { role: 'employee_office', label: 'Employee — Office', hint: 'W-2 scoped CRM; add Office Manager designation for full employee access.' },
  { role: 'admin', label: 'Admin', hint: 'Same as Employee — Full, plus user administration.' },
  { role: 'staff', label: 'Field staff', hint: 'Legacy label — same scoped CRM as Employee — Shop; may be 1099.' },
  { role: 'designer', label: 'Designer', hint: 'Designer Portal + Project Hub comments.' },
  { role: 'architect', label: 'Architect', hint: 'Architect Portal + Design files. Attach projects now so they are waiting on first sign-in.' },
  { role: 'engineer', label: 'Engineer', hint: 'Engineer portal + Project Hub comments.' },
  { role: 'cpa', label: 'CPA', hint: 'Finances only. Comments allowed, numbers stay read-only.' },
  { role: 'supplier', label: 'Supplier', hint: 'Vendor portal. Set access (Owner vs Employee) and work (Timber / CNC).' },
];

const DESIGNATION_LABELS: Record<EmployeeDesignation, string> = {
  project_manager: 'Project Manager',
  sales_rep: 'Sales Rep',
  estimator: 'Estimator',
  executive_assistant: 'Executive Assistant',
  cfo: 'CFO',
  coo: 'COO',
  office_manager: 'Office Manager',
};

export interface UnifiedInviteModalProps {
  onClose: () => void;
  defaultRole?: UnifiedInviteRole;
  lockRole?: boolean;
  defaultSupplierOrgId?: string;
  lockSupplierOrg?: boolean;
  /** Shared machine login — not a named teammate. */
  workstation?: boolean;
}

const UnifiedInviteModal: React.FC<UnifiedInviteModalProps> = ({
  onClose,
  defaultRole = 'employee',
  lockRole = false,
  defaultSupplierOrgId,
  lockSupplierOrg = false,
  workstation = false,
}) => {
  const { user } = useAuth();
  const {
    supplierOrgs,
    createEmployeeInvite,
    createArchitectInvite,
    createEngineerInvite,
    createCpaInvite,
    createSupplierInvite,
    timberProjects,
    enhancedDeals,
  } = useCRM();

  const [role, setRole] = useState<UnifiedInviteRole>(
    workstation ? (defaultRole === 'employee' ? 'employee' : 'staff') : defaultRole,
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [usernameDirty, setUsernameDirty] = useState(false);
  const [designations, setDesignations] = useState<EmployeeDesignation[]>([]);
  const [supplierOrgId, setSupplierOrgId] = useState(defaultSupplierOrgId || '');
  const [supplierFlags, setSupplierFlags] = useState<SupplierWorkFlag[]>([]);
  const [supplierPortalRole, setSupplierPortalRole] = useState<SupplierPortalRole>('owner');
  const [sending, setSending] = useState(false);
  const [projectQuery, setProjectQuery] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    tempPassword?: string;
    setupLink?: string;
    email?: string;
    portalUrl?: string;
    inviteUrl?: string;
  } | null>(null);

  const orgs = useMemo(
    () => [...supplierOrgs].sort((a, b) => a.name.localeCompare(b.name)),
    [supplierOrgs],
  );
  const selectedOrg = orgs.find((o) => o.id === supplierOrgId);

  useEffect(() => {
    if (role !== 'supplier' || !supplierOrgId) return;
    const org = orgs.find((o) => o.id === supplierOrgId);
    if (org && supplierFlags.length === 0) {
      setSupplierFlags(supplierWorkFlagsFromOrgCapabilities(org.capabilities));
    }
  }, [role, supplierOrgId, orgs, supplierFlags.length]);

  useEffect(() => {
    if (!workstation || usernameDirty) return;
    const derived = normalizeWorkstationUsername(name);
    if (derived) setEmail(derived);
  }, [name, workstation, usernameDirty]);

  const applyOrgDefaults = (orgId: string) => {
    setSupplierOrgId(orgId);
    const org = orgs.find((o) => o.id === orgId);
    if (org) setSupplierFlags(supplierWorkFlagsFromOrgCapabilities(org.capabilities));
  };

  const roleOptions = workstation
    ? ROLE_OPTIONS.filter((opt) => opt.role === 'staff' || opt.role === 'employee')
    : ROLE_OPTIONS;

  const canSend = name.trim() && (workstation ? resolveInviteAuthEmail(email).ok : !!email.trim()) && !sending && (
    workstation
      ? role === 'staff' || role === 'employee'
      : role !== 'supplier'
        ? true
        : !!supplierOrgId && supplierFlags.length > 0
  );

  const postHttpInvite = async (functionName: string, body: Record<string, string>) => {
    const response = await crmAuthedFetch(`${FUNCTIONS_URL}/${functionName}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<{
      success?: boolean;
      method?: string;
      message?: string;
      error?: string;
      tempPassword?: string;
      setupLink?: string;
      portalUrl?: string;
      inviteUrl?: string;
      authUid?: string;
    }>;
  };

  const openProjects = useMemo(() => (
    timberProjects
      .filter((project) => project.id && project.status !== 'completed')
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  ), [timberProjects]);

  const visibleProjects = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    if (!q) return openProjects;
    return openProjects.filter((project) => (project.name || '').toLowerCase().includes(q));
  }, [openProjects, projectQuery]);

  const toggleArchitectProject = (projectId: string) => {
    const ids = architectDealProjectIds(projectId, openProjects);
    setSelectedProjectIds((prev) => {
      const selected = new Set(prev);
      const allOn = ids.every((id) => selected.has(id));
      if (allOn) {
        ids.forEach((id) => selected.delete(id));
      } else {
        ids.forEach((id) => selected.add(id));
      }
      return Array.from(selected);
    });
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    const trimmedName = name.trim();
    const resolvedLogin = workstation
      ? resolveInviteAuthEmail(email)
      : { ok: true as const, email: email.trim().toLowerCase(), loginLabel: email.trim().toLowerCase() };
    if (!resolvedLogin.ok) {
      setResult({ success: false, message: resolvedLogin.error });
      setSending(false);
      return;
    }
    const trimmedEmail = resolvedLogin.email;
    const loginLabel = resolvedLogin.loginLabel;
    const invitedBy = user?.id || '';
    const invitedByName = user?.name || '';
    try {
      if (
        role === 'employee'
        || role === 'employee_shop'
        || role === 'employee_office'
        || role === 'admin'
        || role === 'staff'
      ) {
        const inviteId = await createEmployeeInvite({
          email: trimmedEmail,
          name: trimmedName,
          role: role as EmployeeInviteRole,
          designations: workstation ? [] : designations,
          ...(workstation ? { isWorkstation: true } : {}),
          invitedBy,
          invitedByName,
          status: 'pending',
        });
        const send = httpsCallable(
          getFunctions(undefined, 'us-central1'),
          'sendEmployeeInviteCallable',
        );
        const { data } = await send({
          email: trimmedEmail,
          name: trimmedName,
          inviteId,
          role,
          designations: workstation ? [] : designations,
          isWorkstation: workstation,
        });
        const d = data as {
          success?: boolean;
          method?: string;
          message?: string;
          tempPassword?: string;
          setupLink?: string;
          portalUrl?: string;
        };
        setResult({
          success: true,
          message: d.method === 'email'
            ? `Invite email sent to ${trimmedEmail}`
            : (d.message || 'Share the setup details securely.'),
          tempPassword: d.tempPassword,
          setupLink: d.setupLink,
          email: loginLabel,
          portalUrl: d.portalUrl || 'https://clients.lctimberframes.com',
        });
      } else if (role === 'architect' || role === 'designer' || role === 'engineer') {
        const createFn = role === 'engineer' ? createEngineerInvite : createArchitectInvite;
        const functionName = role === 'engineer' ? 'sendEngineerInvite' : 'sendArchitectInvite';
        const inviteId = await createFn({
          email: trimmedEmail,
          name: trimmedName,
          invitedBy,
          invitedByName,
          status: 'pending',
          ...(role === 'engineer' ? {} : { inviteKind: role === 'architect' ? 'architect' : 'designer' }),
        });
        const data = await postHttpInvite(functionName, {
          email: trimmedEmail,
          name: trimmedName,
          inviteId,
        });
        if (!data.success) {
          setResult({ success: false, message: data.error || 'Failed to send invite' });
        } else {
          let projectNote = '';
          if (role === 'architect' && selectedProjectIds.length > 0) {
            let authUid = typeof data.authUid === 'string' ? data.authUid.trim() : '';
            if (!authUid) {
              const inviteSnap = await getDoc(doc(db, 'architectInvites', inviteId));
              const stored = inviteSnap.data()?.authUid;
              authUid = typeof stored === 'string' ? stored.trim() : '';
            }
            if (!authUid) {
              projectNote = ' The account was not ready to attach projects. Add them from Edit project.';
            } else {
              try {
                const count = await grantArchitectPortalProjects({
                  uid: authUid,
                  projectIds: selectedProjectIds,
                  projects: timberProjects,
                  deals: enhancedDeals,
                });
                projectNote = count === 1
                  ? ' They will see 1 project the first time they open the Architect Portal.'
                  : ` They will see ${count} projects the first time they open the Architect Portal.`;
              } catch (grantErr) {
                console.error('Architect project grant failed:', grantErr);
                projectNote = ' The invite was sent, but the projects could not be attached. Add them from Edit project.';
              }
            }
          }
          setResult({
            success: true,
            message: `${data.method === 'email'
              ? `Invite email sent to ${trimmedEmail}`
              : (data.message || 'Share the setup details securely.')}${projectNote}`,
            setupLink: data.setupLink,
            tempPassword: data.tempPassword,
            email: trimmedEmail,
            portalUrl: data.portalUrl || (role === 'architect' ? `https://clients.lctimberframes.com/invite/${inviteId}` : 'https://clients.lctimberframes.com'),
            inviteUrl: data.inviteUrl || (role === 'architect' ? `https://clients.lctimberframes.com/invite/${inviteId}` : undefined),
          });
        }
      } else if (role === 'cpa') {
        const inviteId = await createCpaInvite({
          email: trimmedEmail,
          name: trimmedName,
          invitedBy,
          invitedByName,
          status: 'pending',
        });
        const data = await postHttpInvite('sendCpaInvite', {
          email: trimmedEmail,
          name: trimmedName,
          inviteId,
        });
        if (!data.success) {
          setResult({ success: false, message: data.error || 'Failed to send invite' });
        } else {
          setResult({
            success: true,
            message: data.method === 'email'
              ? `Invite email sent to ${trimmedEmail}`
              : (data.message || 'Share the setup details securely.'),
            setupLink: data.setupLink,
            email: trimmedEmail,
            portalUrl: data.portalUrl || 'https://clients.lctimberframes.com',
          });
        }
      } else {
        const inviteId = await createSupplierInvite({
          email: trimmedEmail,
          name: trimmedName,
          supplierOrgId,
          supplierDesignations: supplierFlags,
          supplierPortalRole,
          invitedBy,
          invitedByName,
          status: 'pending',
        });
        const call = httpsCallable(
          getFunctions(undefined, 'us-central1'),
          'sendSupplierInviteCallable',
        );
        const { data } = await call({
          email: trimmedEmail,
          name: trimmedName,
          inviteId,
        });
        const d = data as {
          success?: boolean;
          method?: string;
          message?: string;
          setupLink?: string;
          portalUrl?: string;
        };
        setResult({
          success: !!d.success,
          message: d.method === 'email' || d.method === 'simulated'
            ? (d.message || `Invite email sent to ${trimmedEmail}`)
            : (d.message || (d.success ? 'Invite processed.' : 'Invite failed')),
          setupLink: d.setupLink,
          email: trimmedEmail,
          portalUrl: d.portalUrl || 'https://clients.lctimberframes.com/vendor-portal',
        });
      }
    } catch (err) {
      console.error('Unified invite failed:', err);
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Network error. Check your connection.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="um-unified-invite-title">
        <div className="um-modal-header">
          <h3 id="um-unified-invite-title">{workstation ? 'Add workstation' : 'Invite teammate'}</h3>
          <button type="button" className="um-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="um-modal-body">
          <p className="um-hours-help">
            {workstation
              ? 'Shared login for a machine (Job Trailer, Cadwork PC, Shop Floor). It will not appear as a person on the org chart or project team.'
              : 'One invite for employees, designers, engineers, CPAs, and suppliers. Temporary password is emailed when SMTP is configured.'}
          </p>
          <div className="um-field">
            <label>Role</label>
            <div className="um-role-selector">
              {roleOptions.map((opt) => (
                <button
                  key={opt.role}
                  type="button"
                  className={`um-role-option ${role === opt.role ? 'um-role-selected' : ''}`}
                  disabled={lockRole && opt.role !== role}
                  onClick={() => {
                    if (lockRole) return;
                    setRole(opt.role);
                    if (opt.role === 'supplier' && supplierOrgId && selectedOrg) {
                      setSupplierFlags(supplierWorkFlagsFromOrgCapabilities(selectedOrg.capabilities));
                    }
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="um-hours-help" style={{ marginTop: 8 }}>
              {roleOptions.find((o) => o.role === role)?.hint}
            </p>
          </div>
          <div className="um-field">
            <label htmlFor="um-invite-name">{workstation ? 'Workstation name' : 'Full name'}</label>
            <input
              id="um-invite-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={workstation ? 'e.g. Job Trailer' : 'e.g. Jordan Hale'}
              autoComplete="name"
            />
          </div>
          <div className="um-field">
            <label htmlFor="um-invite-email">{workstation ? 'Username' : 'Email'}</label>
            <input
              id="um-invite-email"
              type={workstation ? 'text' : 'email'}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (workstation) setUsernameDirty(true);
              }}
              placeholder={workstation ? 'job-trailer' : 'name@company.com'}
              autoComplete={workstation ? 'off' : 'email'}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {workstation && (
              <p className="um-hours-help" style={{ marginTop: 8 }}>
                Sign in with this username — no mailbox needed. A real email still works if you add one later.
              </p>
            )}
          </div>
          {role === 'architect' && (
            <div className="um-field">
              <label htmlFor="um-architect-project-search">Projects</label>
              <p className="um-hours-help" style={{ marginBottom: 8 }}>
                Optional. They will see these projects the first time they open the Architect Portal, before they accept. Projects on the same deal are included together.
              </p>
              <input
                id="um-architect-project-search"
                type="search"
                value={projectQuery}
                onChange={(e) => setProjectQuery(e.target.value)}
                placeholder="Search projects"
              />
              <div className="um-architect-projects" role="group" aria-label="Projects for this architect">
                {visibleProjects.length === 0 ? (
                  <p className="um-hours-help">No open projects match.</p>
                ) : visibleProjects.map((project) => {
                  const id = project.id || '';
                  return (
                    <label key={id} className="um-architect-project">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(id)}
                        onChange={() => toggleArchitectProject(id)}
                      />
                      <span>{project.name || 'Untitled project'}</span>
                    </label>
                  );
                })}
              </div>
              {selectedProjectIds.length > 0 && (
                <p className="um-hours-help" style={{ marginTop: 8 }}>
                  {selectedProjectIds.length === 1
                    ? '1 project selected.'
                    : `${selectedProjectIds.length} projects selected.`}
                </p>
              )}
            </div>
          )}
          {!workstation && (role === 'employee' || role === 'admin' || role === 'employee_office' || role === 'employee_shop' || role === 'staff') && (
            <div className="um-field">
              <label>Designations</label>
              <div className="um-designation-toggles">
                {EMPLOYEE_DESIGNATIONS.map((key) => {
                  const active = designations.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                      onClick={() => {
                        setDesignations((prev) =>
                          active ? prev.filter((x) => x !== key) : [...prev, key],
                        );
                      }}
                    >
                      {DESIGNATION_LABELS[key]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {role === 'supplier' && (
            <>
              <div className="um-field">
                <label htmlFor="um-invite-org">Vendor organization</label>
                <select
                  id="um-invite-org"
                  value={supplierOrgId}
                  disabled={lockSupplierOrg}
                  onChange={(e) => applyOrgDefaults(e.target.value)}
                >
                  <option value="">Select a vendor…</option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="um-field">
                <label>Access</label>
                <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                  Owner sees quotes and totals. Employee can use files, drawings, comments, and production — not prices or invoices.
                </p>
                <div className="um-designation-toggles">
                  {(['owner', 'employee'] as SupplierPortalRole[]).map((level) => {
                    const active = supplierPortalRole === level;
                    return (
                      <button
                        key={level}
                        type="button"
                        className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                        onClick={() => setSupplierPortalRole(level)}
                      >
                        {supplierPortalRoleLabel(level)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="um-field">
                <label>Work</label>
                <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                  Required. After a quote is awarded, only matching work can open Production comments. Timber-only users stay out of CNC-awarded jobs.
                </p>
                <div className="um-designation-toggles">
                  {(['timber_supplier', 'cnc_supplier'] as SupplierWorkFlag[]).map((flag) => {
                    const active = supplierFlags.includes(flag);
                    return (
                      <button
                        key={flag}
                        type="button"
                        className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                        onClick={() => {
                          setSupplierFlags((prev) =>
                            active ? prev.filter((x) => x !== flag) : [...prev, flag],
                          );
                        }}
                      >
                        {supplierWorkFlagLabel(flag)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          {result && (
            <div className={`um-invite-result ${result.success ? 'is-success' : 'is-error'}`}>
              {result.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
              <div>
                <p>{result.message}</p>
                {(result.tempPassword || result.setupLink || result.inviteUrl) && (
                  <div className="um-invite-creds">
                    <div><span>Portal</span> {result.portalUrl}</div>
                    {result.inviteUrl && (
                      <div><span>Invite link</span> {result.inviteUrl}</div>
                    )}
                    <div><span>{workstation ? 'Username' : 'Email'}</span> {result.email}</div>
                    {result.tempPassword && (
                      <div><span>Temporary password</span> <code>{result.tempPassword}</code></div>
                    )}
                    {result.setupLink && (
                      <div><span>Setup link</span> <code style={{ wordBreak: 'break-all' }}>{result.setupLink}</code></div>
                    )}
                    <button
                      type="button"
                      className="um-btn-secondary"
                      onClick={() => {
                        const lines = [
                          `Sign in at: ${result.portalUrl}`,
                          `${workstation ? 'Username' : 'Email'}: ${result.email}`,
                        ];
                        if (result.inviteUrl) lines.push(`Invite: ${result.inviteUrl}`);
                        if (result.tempPassword) lines.push(`Temporary Password: ${result.tempPassword}`);
                        if (result.setupLink) lines.push(`Setup link: ${result.setupLink}`);
                        navigator.clipboard.writeText(lines.join('\n'));
                      }}
                    >
                      <Copy size={12} /> Copy details
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="um-modal-footer">
          <button type="button" className="um-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="um-btn-save"
            onClick={() => void handleSend()}
            disabled={!canSend}
          >
            {sending ? 'Sending…' : <><Send size={13} /> {workstation ? 'Create login' : 'Send invite'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedInviteModal;
